import {AfterContentInit, Component, Input, isDevMode, OnDestroy} from '@angular/core';
import {ContentChildren, QueryList} from '@angular/core';
import {Subscription} from 'rxjs';
import {ClTabComponent} from './cl-tab/cl-tab.component';

/** Two years, matching WhatsNewService -- a remembered tab is a preference, not session state. */
const REMEMBER_MAX_AGE = 60 * 60 * 24 * 730;

/** The castLabs UI styles .cl-tab-1 to .cl-tab-16. */
const MAX_TABS = 16;

/** Per-instance ids, so the radios of two tab strips on one page never share a name. */
let nextUid = 0;

@Component({
  selector: 'app-cl-tabs',
  templateUrl: './cl-tabs.component.html',
  standalone: false
})
export class ClTabsComponent implements AfterContentInit, OnDestroy {
  @ContentChildren(ClTabComponent) tabs: QueryList<ClTabComponent>;

  /**
   * Remember the chosen tab across reloads, under this name.
   *
   * Opt-in, because most tab strips are throwaway. Set it and every tab that carries a `key` can be
   * restored; tabs without one stay unrestorable, which is what keeps the cookie meaningful.
   *
   * A cookie rather than localStorage, matching WhatsNewService and MonitoringAccessService.
   */
  @Input() rememberAs: string | null = null;

  /**
   * Set once the selection is final -- either the user clicked, or the remembered tab was honoured.
   *
   * The tab set is not stable at content-init: the tabs are behind permission and licence checks
   * that resolve asynchronously, so the remembered one may not exist yet when we first look. Until
   * this is set we keep re-checking as tabs appear, which is the difference between the feature
   * working and silently falling back to the first tab for anyone whose permissions arrive late.
   */
  private settled = false;
  private subscription = new Subscription();

  /** Prefix of the radios' ids and their shared name. */
  readonly uid = `cl-tabs-${nextUid++}`;

  ngAfterContentInit() {
    this._number();
    this._syncSelection();
    // Late-arriving tabs get the same chance to be the remembered one, and every tab its position:
    // the library pairs radio, label and panel by the cl-tab-N class. It styles N = 1..16.
    this.subscription.add(this.tabs.changes.subscribe(() => {
      this._number();
      this._syncSelection();
    }));
  }

  private _number(): void {
    if (isDevMode() && this.tabs.length > MAX_TABS) {
      console.error(`app-cl-tabs: ${this.tabs.length} tabs, but the castLabs UI styles only cl-tab-1..${MAX_TABS}`);
    }
    this.tabs.forEach((tab, i) => tab.position.set(i + 1));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /** A click or arrow key is the user's own choice, so it settles the selection and is what gets remembered. */
  selectTab(tab: ClTabComponent) {
    this.settled = true;
    this._activate(tab);
    this._remember(tab);
  }

  /**
   * A click on a tab's label or radio, including on the tab that is already selected, which fires
   * no change event: still a choice, so it settles the selection, is remembered and emits
   * (activated), as a click on the old tab did. A click on another tab emits through selectTab().
   */
  confirmTab(tab: ClTabComponent) {
    this.settled = true;
    this._remember(tab);
    // A click on the tab that is already selected fires no change, so selectTab() does not run;
    // (activated) still fires for it, as it did for every click on the old tab strip.
    if (tab.active()) {
      tab.activated.emit();
    }
  }

  private _activate(tab: ClTabComponent) {
    // deactivate all tabs
    this.tabs.toArray().forEach(_tab => _tab.active.set(false));
    // activate the tab the user has clicked on.
    tab.active.set(true);
    tab.activated.emit()
  }

  /**
   * Restore the remembered tab if it is available, otherwise fall back to the first one.
   *
   * The fallback deliberately does not settle: a tab strip that renders "IFE Workflow" first and
   * gains "VOD Default" a tick later would otherwise strand the user on the fallback, which is the
   * common case rather than an edge one.
   */
  private _syncSelection(): void {
    if (this.settled || !this.tabs.length) {
      return;
    }
    const remembered = this._rememberedKey();
    const match = remembered ? this.tabs.find(tab => tab.key === remembered) : undefined;
    if (match) {
      this.settled = true;
      this._activate(match);
      return;
    }
    if (!this.tabs.some(tab => tab.active())) {
      this._activate(this.tabs.first);
    }
  }

  /** Only a real choice is written: persisting the fallback would erase what was remembered. */
  private _remember(tab: ClTabComponent): void {
    if (!this.rememberAs || !tab.key) {
      return;
    }
    document.cookie =
      `${this._cookieName()}=${encodeURIComponent(tab.key)}` +
      `; path=/; max-age=${REMEMBER_MAX_AGE}; SameSite=Lax`;
  }

  private _rememberedKey(): string | null {
    if (!this.rememberAs) {
      return null;
    }
    const prefix = `${this._cookieName()}=`;
    const cookie = document.cookie.split('; ').find(c => c.startsWith(prefix));
    return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
  }

  private _cookieName(): string {
    return `cp-tab-${this.rememberAs}`;
  }

}
